import {
  Component,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
} from '@stencil/core';
import {
  Context,
  Payload,
  Placement,
  RenderedWidget,
  SiteContext,
  WidgetRecommendations,
} from '../../entities';
import { Once } from '../../decorators/decorators';

@Component({
  tag: 'me-widget-loader',
  shadow: false,
  styleUrl: 'me-widget-loader.scss',
})
export class MeWidgetLoader {
  @Prop() backend: string;

  @Event() meWidgetLoaderReady: EventEmitter<(value: SiteContext) => void>;

  private loadedPlacements = new WeakSet<HTMLMeWidgetPlacementElement>();

  async componentDidLoad() {
    await this.loadNewPlacements();
  }

  render() {
    return <Host class="me-widget-loader" />;
  }

  @Method()
  async loadNewPlacements(): Promise<void> {
    const placements = await this.placements(true);

    if (placements.length === 0) {
      return;
    }

    const context = await this.context(placements);
    const widgets = await this.widgets(context);

    for (const widget of widgets) {
      const selector = `me-widget-placement#${widget.placementId}`;
      const placementElement =
        document.querySelector<HTMLMeWidgetPlacementElement>(selector);

      if (!placementElement) {
        continue;
      }

      this.loadedPlacements.add(placementElement);

      placementElement.widget = widget;
    }
  }

  @Method()
  async widgets(context: Context = null): Promise<RenderedWidget[]> {
    const endpoint = new URL('/api/v1/widget/recommendations', this.backend);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(context ?? (await this.context())),
    });

    if (!response?.ok) {
      throw new Error('Failed to load widgets.');
    }

    const payload: Payload<WidgetRecommendations> = await response.json();

    return payload.data.widgets;
  }

  @Method()
  async context(placements: Placement[] = null): Promise<Context> {
    return {
      placements: placements ?? (await this.placements()),
      ...(await this.siteContext()),
    };
  }

  @Method()
  async placements(onlyNew = false): Promise<Placement[]> {
    return Array.from(document.querySelectorAll('me-widget-placement'))
      .filter(element => !onlyNew || !this.loadedPlacements.has(element))
      .map(element => ({
        id: element.id,
      }));
  }

  @Method()
  @Once()
  async siteContext(): Promise<SiteContext> {
    return await new Promise<SiteContext>(resolve => {
      this.meWidgetLoaderReady.emit(resolve);
    });
  }
}
